import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { Field, Textarea } from '../../components/ui/Field';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getIndividualTemplateData, sendEmailNotification } from '../../actions/emailTemplate';
import { getIndividualWhatsAppTemplateData, sendWhatsAppNotification } from '../../actions/whatsappNotification';
import './admin.css';

/**
 * Bulk send for an email or WhatsApp template.
 *
 * Recipients are supplied as JSON — one object per recipient carrying the
 * template's variables — because the variable set differs per template and the API
 * validates that every required key is present. The screen shows the required keys
 * and a ready-made sample so the shape is never a guess.
 */
export default function SendTemplate({ channel = 'email' }) {
  const { id } = useParams();
  const { confirm, toast } = useOverlays();
  const isEmail = channel === 'email';

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const loader = isEmail ? getIndividualTemplateData : getIndividualWhatsAppTemplateData;
    loader(id)
      .then((data) => {
        setTemplate(data);
        const variables = data?.variables || {};
        const sample = isEmail
          ? [{ email: 'someone@example.com', variables }]
          : [{ mobile_number: '+919876543210', variables }];
        setRaw(JSON.stringify(sample, null, 2));
      })
      .catch(() => toast('Could not load the template', { tone: 'error' }))
      .finally(() => setLoading(false));
  }, [id, isEmail, toast]);

  const parsed = useMemo(() => {
    try {
      const value = JSON.parse(raw);
      return Array.isArray(value) ? value : null;
    } catch {
      return null;
    }
  }, [raw]);

  const requiredKeys = useMemo(() => Object.keys(template?.variables || {}), [template]);

  const validate = useCallback(() => {
    if (!parsed) return 'Recipients must be a JSON array.';
    if (parsed.length === 0) return 'Add at least one recipient.';
    const idKey = isEmail ? 'email' : 'mobile_number';
    for (let i = 0; i < parsed.length; i += 1) {
      const entry = parsed[i];
      if (!entry[idKey]) return `Recipient ${i + 1} is missing "${idKey}".`;
      const provided = Object.keys(entry.variables || {});
      const missing = requiredKeys.filter((k) => !provided.includes(k));
      if (missing.length) return `Recipient ${i + 1} is missing variables: ${missing.join(', ')}.`;
    }
    return '';
  }, [parsed, isEmail, requiredKeys]);

  const send = useCallback(async () => {
    const problem = validate();
    setError(problem);
    if (problem) return;

    const ok = await confirm({
      title: `Send to ${parsed.length} ${parsed.length === 1 ? 'recipient' : 'recipients'}?`,
      body: `“${template?.template_alias}” goes out immediately over ${
        isEmail ? 'email' : 'WhatsApp'
      }. Sends cannot be recalled.`,
      confirmLabel: `Send ${parsed.length}`,
      destructive: true,
    });
    if (!ok) return;

    setBusy(true);
    try {
      const payload = { template_id: id, metadata: parsed };
      const res = isEmail ? await sendEmailNotification(payload) : await sendWhatsAppNotification(payload);
      toast(res?.message || `Queued ${parsed.length} messages`, { tone: 'success' });
    } catch (err) {
      toast(err?.response?.data?.error || 'Send failed', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [validate, parsed, confirm, template, isEmail, id, toast]);

  return (
    <>
      <PageHeader
        section="Messaging"
        title={template?.template_alias || 'Send template'}
        subtitle={template?.description}
        back={isEmail ? 'email templates' : 'WhatsApp templates'}
        backTo={isEmail ? '/email-template' : '/whatsapp-template'}
      />

      <div className="sv-screen-body">
        <div className="sv-send">
          <section className="sv-card sv-send__preview">
            <div className="sv-send__preview-head">
              {isEmail ? (
                <>
                  <strong>{template?.subject || 'No subject'}</strong>
                  <div style={{ marginTop: 4 }}>
                    From {template?.sender_name} &lt;{template?.sender_id}&gt;
                  </div>
                </>
              ) : (
                <>
                  <StatusPill tone="info">{template?.category || 'Template'}</StatusPill>
                  <span style={{ marginLeft: 8 }}>{template?.content_type}</span>
                </>
              )}
            </div>

            {loading ? (
              <div style={{ padding: 24 }}>
                <SkeletonBlock width="80%" height={14} />
              </div>
            ) : isEmail ? (
              <div className="sv-send__preview-body">
                <p>{template?.subject}</p>
                <p style={{ color: 'var(--text-muted)' }}>
                  The rendered body lives in SendGrid. Variables below are substituted per recipient
                  at send time.
                </p>
                <ul>
                  {requiredKeys.map((k) => (
                    <li key={k}>
                      <code>{`{{${k}}}`}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="sv-send__preview-body">
                <div className="sv-phone-preview">
                  <div className="sv-phone-preview__bubble">
                    {requiredKeys.length
                      ? requiredKeys.map((k) => `{{${k}}}`).join(' · ')
                      : template?.description || 'Template body'}
                  </div>
                </div>
              </div>
            )}
          </section>

          <aside className="sv-send__side">
            <div className="sv-card sv-rail-card">
              <h3 className="sv-rail-card__title">Required variables</h3>
              {requiredKeys.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  This template takes no variables.
                </p>
              )}
              <div className="sv-tag-list">
                {requiredKeys.map((k) => (
                  <span className="sv-tag" key={k}>
                    {k}
                  </span>
                ))}
              </div>
            </div>

            <div className="sv-card sv-rail-card">
              <h3 className="sv-rail-card__title">Recipients</h3>
              <Field
                label="JSON array"
                error={error}
                hint={`One object per recipient, each with ${isEmail ? '"email"' : '"mobile_number"'} and "variables".`}
              >
                <Textarea
                  rows={12}
                  value={raw}
                  onChange={(e) => {
                    setRaw(e.target.value);
                    setError('');
                  }}
                  invalid={!!error}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                />
              </Field>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 12,
                }}
              >
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {parsed ? `${parsed.length} recipients` : 'Invalid JSON'}
                </span>
                <Button
                  variant="primary"
                  icon="bx-send"
                  onClick={send}
                  loading={busy}
                  disabled={!parsed || parsed.length === 0}
                >
                  Send
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
